import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ShieldAlert, AlertTriangle, Info, Wrench, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';

// Funnel check (backend bundle_audit.py): what a downloaded funnel still carries
// from its original owner, with the two one-click fixes. kind = landing-pages |
// pre-landings | offers; item = {id, name}; null closes the dialog.
const MESSAGES = {
  "foreign_handler": "The form handler sends leads to another server: {hosts}",
  "form_external": "The form posts straight to another site: {hosts}",
  "no_form_tag": "Lead fields without a form tag: lead.js cannot catch them. Replace the handler or add a form tag.",
  "no_catcher": "The lead form is not connected to the tracker",
  "missing_handler": "The form posts to {name}, which is not in the bundle",
  "foreign_tracker": "Script of another tracker: {name}",
  "pixel": "{name} with a foreign ID: {id}",
  "hidden_affiliate": "Hidden field {name} = {value}: probably an ID of the original owner",
  "foreign_redirect": "Redirect to another domain: {hosts}",
  "broken_link": "Link to a file that is not in the bundle: {name}",
  "foreign_macro": "Macro of another tracker: {name}",
  "unused_page": "No other page links here: probably left over from the original funnel",
  "junk_files": "System files from macOS / Windows: {count}"
};

const SEVERITY = {
  high: { label: 'Critical', icon: ShieldAlert, cls: 'text-destructive' },
  warn: { label: 'Warning', icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400' },
  info: { label: 'Note', icon: Info, cls: 'text-muted-foreground' },
};

export default function BundleAudit({ kind, item, onClose }) {
  const { t } = useT();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!item) return;
    setLoading(true);
    try {
      const res = await axios.get(API + '/' + kind + '/' + item.id + '/audit');
      setResult(res.data);
    } catch (err) {
      setResult(null);
      toast.error((err.response && err.response.data && err.response.data.detail) || t('Could not check the funnel'));
    } finally {
      setLoading(false);
    }
  }, [kind, item, t]);

  useEffect(() => { setResult(null); if (item) load(); }, [item, load]);

  const fix = async (action, file, key) => {
    setBusy(key);
    try {
      if (action === 'delete_file' && !window.confirm(t('Delete this file?') + ' ' + file)) { setBusy(''); return; }
      const body = file ? { action, file } : { action };
      const res = await axios.post(API + '/' + kind + '/' + item.id + '/audit/fix', body);
      setResult(res.data.audit);
      toast.success(t('Fixed') + ': ' + (res.data.changed || []).join(', '));
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.detail) || t('Could not check the funnel'));
    } finally {
      setBusy('');
    }
  };

  const findings = (result && result.findings) || [];
  const uncaught = findings.filter((f) => f.code === 'no_catcher').length;

  return (
    <Dialog open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Funnel check')}{item ? ' — ' + item.name : ''}</DialogTitle>
          <DialogDescription>{t('Funnels from spy services often keep parts of the original owner: a form handler, trackers, pixels and IDs. The buttons fix what can be fixed automatically; remove the rest in Edit files.')}</DialogDescription>
        </DialogHeader>
        {loading && !result ? <div className="text-sm text-muted-foreground py-6">{t('Checking…')}</div> : null}
        {result ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="destructive">{t('Critical')}: {result.summary.high}</Badge>
              <Badge variant="outline">{t('Warning')}: {result.summary.warn}</Badge>
              <Badge variant="outline">{t('Note')}: {result.summary.info}</Badge>
              <span className="text-muted-foreground">{t('Files checked')}: {result.files_checked}</span>
              <div className="ml-auto flex gap-2">
                {uncaught > 1 ? (
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => fix('inject_lead_js', null, 'all')}>
                    <Wrench className="w-4 h-4 mr-1" />{t('Insert lead.js into all pages')}
                  </Button>
                ) : null}
                <Button size="sm" variant="ghost" onClick={load} disabled={loading} aria-label={t('Check again')} title={t('Check again')}>
                  <RefreshCw className={'w-4 h-4' + (loading ? ' animate-spin' : '')} />
                </Button>
              </div>
            </div>
            {findings.length === 0 ? (
              <div className="flex items-center gap-2 text-sm py-4"><ShieldCheck className="w-5 h-5 text-primary" />{t('No problems found')}</div>
            ) : (
              <ul className="space-y-2">
                {findings.map((f, i) => {
                  const sev = SEVERITY[f.severity] || SEVERITY.info;
                  const Icon = sev.icon;
                  const key = f.code + ':' + f.file + ':' + f.line + ':' + i;
                  return (
                    <li key={key} className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
                      <Icon className={'w-4 h-4 mt-0.5 shrink-0 ' + sev.cls} aria-label={t(sev.label)} />
                      <div className="min-w-0 flex-1">
                        <div className="break-words">{t(MESSAGES[f.code] || f.code, { hosts: f.hosts || '', name: f.name || '', id: f.id || '', value: f.value || '', count: f.count || '' })}</div>
                        <div className="font-mono text-xs text-muted-foreground">{f.file}:{f.line}</div>
                      </div>
                      {f.fix ? (
                        <Button size="sm" variant="outline" className="shrink-0" disabled={!!busy} onClick={() => fix(f.fix.action, f.fix.file, key)}>
                          <Wrench className="w-4 h-4 mr-1" />
                          {f.fix.action === 'replace_handler' ? t('Replace with the XPNSR api.php')
                            : f.fix.action === 'delete_file' ? t('Delete file')
                            : f.fix.action === 'delete_junk' ? t('Delete junk files')
                            : t('Insert lead.js')}
                        </Button>
                      ) : null}
                      {f.file && f.file !== result.index_file && f.code !== 'junk_files' && !(f.fix && f.fix.action === 'delete_file') ? (
                        <Button size="sm" variant="ghost" className="shrink-0" disabled={!!busy} onClick={() => fix('delete_file', f.file, key + ':del')} aria-label={t('Delete file')} title={t('Delete file')}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
